import {
  Card,
  Skeleton,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  buttonVariants,
} from "@attmos/flowstack-ui";
import { FolderKanban, LayoutGrid, ListChecks } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";

import { portalLaunchUrl, useApps, useInAppCards } from "@/api/apps";
import { useSession } from "@/api/auth";
import {
  type Project,
  type ProjectListResponse,
  type ProjectRun,
  useProjectList,
  wholeProjectList,
} from "@/api/projects";
import { useRunCounts, useRuns } from "@/api/runs";
import { InAppCard } from "@/components/apps/InAppCard";
import { LogoWell } from "@/components/apps/LogoWell";
import { relativeTime } from "@/components/grid/cells";
import { EmptyState } from "@/components/layout/EmptyState";
import { PageHeader } from "@/components/layout/PageHeader";
import { SUBMIT_ICONS } from "@/components/projects/submit-icons";
import { elapsedLabel } from "@/components/runs/run-header-meta";
import { RunStateBadge } from "@/components/runs/run-state-badge";
import { absentTimingLabel } from "@/components/runs/run-timing";
import { dashboardTitle } from "@/routes/dashboard/greeting";
import { apiErrorMessage, classifyApiError } from "@/lib/api-error-kind";
import { formatCount } from "@/lib/format-count";
import { cn } from "@/lib/utils";
import { heroCard } from "@/routes/dashboard/hero-card";
import { rememberedAppFor, useLastAppStore } from "@/stores/last-app";

/**
 * The dashboard (flowstack#945, laid out to the flowstack#1976 design) —
 * the landing surface, answering "what is happening with my work right
 * now?": a header with the two submit actions, the ATTMOS Suite apps as
 * logos, a summary strip, and the latest runs as a table.
 *
 * Three rules shape everything below.
 *
 * **It summarises; it never computes.** Every number here is a field
 * some endpoint already returns, or a rollup over a list the response
 * PROVES is complete (`/api/projects` with its `total`, flowstack#1866).
 * Nothing is assembled by paging the browser through a bounded feed — a
 * total derived that way is slow and quietly wrong the moment the data
 * outgrows the page size. The two 7-day tiles read `/api/run-counts`
 * (flowstack#1983), which the server counts. Where a KPI has no source,
 * it is ABSENT and filed, not faked:
 *
 * - **who submitted a run** — the `runs` table has no user column at
 *   all, and both run routes synthesize `submitted_by` from the
 *   *requesting* session, so every row claims to be yours (#960).
 *   The table therefore carries no "By" column, and this page is scoped
 *   and worded as your TENANT's work, not "your" work.
 * - **artifact counts** — `/api/projects` reports a real
 *   `artifact_count` (#961), but it is the tenant's whole catalog while
 *   projects are single (#515), so it is not summed here.
 *
 * **It is a summary, not a second source of truth.** Every panel links
 * to the real page and every row links to its own detail route. Nothing
 * is reachable ONLY from here.
 *
 * **Each panel fails alone.** The three reads are independent queries
 * rendered through independent state machines (loading / error / empty
 * / content), so a 500 on one endpoint degrades one card instead of
 * blanking the landing page. Errors use the same four-way
 * classification the Analysis surface uses (`classifyApiError`,
 * flowstack#923) rather than one generic "something went wrong".
 *
 * Scoping is the session's tenant, enforced server-side by
 * `SessionDep` → `tenant_context` on every endpoint read here — the
 * same path the Analysis surface uses. `tests/server/
 * test_dashboard_scoping.py` pins the negative case.
 */
export function DashboardPage(): JSX.Element {
  const { data: session } = useSession();
  const projectList = useProjectList();
  const current = currentProject(projectList.data?.projects);

  const where = [session?.tenant_display_name, current?.display_name].filter(Boolean).join(" · ");

  // The h1 greets by name when the session has one, so the test id — not
  // the title text — is what identifies this page to the e2e specs.
  return (
    <div data-testid="dashboard-page" className="flex flex-col gap-6">
      <PageHeader
        title={dashboardTitle(session, new Date())}
        description={where ? `Work in ${where}.` : "Your runs, projects and apps."}
        actions={current ? <SubmitActions project={current} /> : undefined}
      />
      <StartWork />
      <SummaryStrip
        data={projectList.data}
        isLoading={projectList.isLoading}
        isError={projectList.isError}
        error={projectList.error}
      />
      <LatestWorkPanel />
    </div>
  );
}

/**
 * The project the header's submit actions target: the one with the most
 * work in flight, then by name — the same order the Projects panel this
 * replaced listed them in. On a pilot tenant there is exactly one.
 */
function currentProject(projects: Project[] | undefined): Project | undefined {
  if (!projects || projects.length === 0) return undefined;
  return [...projects].sort(
    (a, b) =>
      b.active_run_count - a.active_run_count || a.display_name.localeCompare(b.display_name)
  )[0];
}

/** The two primary actions, as LINKS styled as buttons (fs#1797: never a
 * `<button>` inside an `<a>`). `lg` is the 44px target. The secondary
 * action comes first and the primary last, as the design orders them.
 * Each carries the project page's icon for the same action (fs#2000); the
 * icon takes the text colour and stays out of the accessible name. */
function SubmitActions({ project }: { project: Project }): JSX.Element {
  const base = `/projects/${encodeURIComponent(project.slug)}/submit`;
  const TileIcon = SUBMIT_ICONS.tile;
  const FlowIcon = SUBMIT_ICONS.flow;
  return (
    <>
      <Link
        to={`${base}/tile`}
        className={buttonVariants({ variant: "outline", size: "lg", className: "gap-2 px-5" })}
      >
        <TileIcon className="h-4 w-4" aria-hidden />
        Submit a tile
      </Link>
      <Link to={`${base}/flow`} className={buttonVariants({ size: "lg", className: "gap-2 px-5" })}>
        <FlowIcon className="h-4 w-4" aria-hidden />
        Submit a flow
      </Link>
    </>
  );
}

// ---- Panel chrome ---------------------------------------------------

interface PanelProps {
  title: string;
  /** `lg` is a section heading ("Latest work", 18px/600 in the design);
   * `md` is a card title at body size ("ATTMOS Suite", 600). */
  titleSize?: "md" | "lg";
  /** Where "the real page" is — the panel header links here. */
  to: string;
  /** Link text; names the destination so it is unambiguous out of context. */
  linkLabel: string;
  /** A caption under the content, wired to the region's `aria-describedby`. */
  description?: string;
  className?: string;
  children: React.ReactNode;
}

/**
 * One dashboard card. The header's link is what keeps this a summary
 * rather than a second source of truth.
 *
 * Each panel is a labelled `region` landmark, not a bare card: the
 * dashboard is several independent things on one screen, so a user
 * navigating by landmark can reach "Latest work" directly. The label
 * comes from the panel's own heading via `aria-labelledby`, so the two
 * can never drift apart. An optional caption is the region's description
 * the same way; it closes the card, under the content it describes.
 *
 * The heading is sentence case in the ink colour, as the design draws
 * it. The all-caps `label-micro` is the design's for "Start work" only.
 */
function Panel({
  title,
  titleSize = "md",
  to,
  linkLabel,
  description,
  className,
  children,
}: PanelProps): JSX.Element {
  const headingId = React.useId();
  const descriptionId = React.useId();
  return (
    <Card
      elevation="raised"
      role="region"
      aria-labelledby={headingId}
      aria-describedby={description ? descriptionId : undefined}
      className={cn("flex min-w-0 flex-col gap-4 p-card", className)}
    >
      <div className="flex items-baseline justify-between gap-3">
        <h2
          id={headingId}
          className={cn(
            "font-semibold text-foreground",
            titleSize === "lg" ? "font-display text-lg" : "text-base"
          )}
        >
          {title}
        </h2>
        <Link
          to={to}
          className="text-xs font-medium text-accent underline-offset-4 hover:underline"
        >
          {linkLabel}
        </Link>
      </div>
      {children}
      {description ? (
        <p id={descriptionId} className="text-sm text-muted-foreground">
          {description}
        </p>
      ) : null}
    </Card>
  );
}

function PanelSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      {Array.from({ length: 3 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  );
}

/**
 * Four-way failure copy per panel (#923). `subject` names what could
 * not be read, so "we couldn't check" never reads as "there is
 * nothing here" — the two are different claims and only one of them
 * is the user's problem to act on.
 */
function describePanelError(
  error: unknown,
  subject: string
): { title: string; description: string } {
  const detail = apiErrorMessage(error);
  switch (classifyApiError(error)) {
    case "network":
      return {
        title: "Could not reach the server.",
        description: "Check your connection and try again.",
      };
    case "forbidden":
      return { title: `You don't have permission to view ${subject}.`, description: detail };
    case "not-found":
      return { title: `${subject} could not be found.`, description: detail };
    case "server":
      return { title: `The server failed to load ${subject}.`, description: detail };
    default:
      return { title: `Could not load ${subject}.`, description: detail };
  }
}

function PanelError({ error, subject }: { error: unknown; subject: string }): JSX.Element {
  const { title, description } = describePanelError(error, subject);
  return <EmptyState className="py-8" title={title} description={description} />;
}

// ---- Summary strip --------------------------------------------------

interface SummaryStripProps {
  data: ProjectListResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
}

/**
 * The design's four tiles, in its order: in flight now, completed in the
 * last 7 days, need attention (failed in the last 7 days), all runs.
 *
 * In flight and all runs are sums of the per-project counts `/api/projects`
 * computes server-side (`active_run_count`, `run_count`), taken only over a
 * list the response proves whole (`wholeProjectList`). The two 7-day tiles
 * read `/api/run-counts`, a separate query, so they wait and fail on their
 * own and show "—" meanwhile — never a 0 the server did not send. The runs
 * feed is never counted: it is one capped page.
 */
function SummaryStrip({ data, isLoading, isError, error }: SummaryStripProps): JSX.Element {
  const headingId = React.useId();
  let body: React.ReactNode;

  if (isLoading) {
    body = (
      <div className={TILE_GRID}>
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-24" />
        ))}
      </div>
    );
  } else if (isError || !data) {
    body = (
      <Card>
        <PanelError error={error} subject="your projects" />
      </Card>
    );
  } else if (data.projects.length === 0) {
    body = (
      <Card>
        <EmptyState
          className="py-8"
          icon={<FolderKanban className="h-7 w-7" aria-hidden />}
          title="No projects yet."
          description="A project is where runs and their artifacts live. Pilot tenants are seeded with one; contact your FlowStack administrator if you don't see it."
          cta={
            <Link
              to="/projects"
              className="text-sm font-medium text-accent underline-offset-4 hover:underline"
            >
              Go to Projects
            </Link>
          }
        />
      </Card>
    );
  } else {
    const projects = wholeProjectList(data);
    body =
      projects === undefined ? (
        <Card className="p-card">
          <p className="text-sm text-muted-foreground">
            Run totals are unavailable: this server did not confirm the project list is complete.{" "}
            <Link
              to="/projects"
              className="font-medium text-accent underline-offset-4 hover:underline"
            >
              All projects
            </Link>
          </p>
        </Card>
      ) : (
        <SummaryTiles projects={projects} />
      );
  }

  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-2">
      <h2 id={headingId} className="sr-only">
        Summary
      </h2>
      {body}
    </section>
  );
}

/** One column on a phone, two on a tablet, the design's four on a desktop. */
const TILE_GRID = "grid gap-4 sm:grid-cols-2 lg:grid-cols-4";

function SummaryTiles({ projects }: { projects: Project[] }): JSX.Element {
  const counts = useRunCounts();
  const inFlight = projects.reduce((sum, p) => sum + p.active_run_count, 0);
  const allRuns = projects.reduce((sum, p) => sum + p.run_count, 0);
  const across = `across ${countLabel(projects.length, "project")}`;
  // While the 7-day counts load or after they fail, the two tiles keep their
  // place and show "—": a 0 would claim nothing completed or failed.
  const pending: TilePending | undefined = counts.isError
    ? "error"
    : counts.data
      ? undefined
      : "loading";

  return (
    <div className={TILE_GRID}>
      <SummaryTile label="In flight now" value={inFlight} detail={across} />
      <SummaryTile
        label="Completed, last 7 days"
        value={counts.data?.completed}
        pending={pending}
        detail="runs"
      />
      <SummaryTile
        label="Need attention"
        value={counts.data?.failed}
        pending={pending}
        detail="failed, last 7 days"
        attention
      />
      <SummaryTile
        label="All runs"
        value={allRuns}
        detail={projects.length === 1 ? projects[0]?.display_name : across}
      />
    </div>
  );
}

type TilePending = "loading" | "error";

/**
 * One strip tile: a labelled `group`, so the number is read with its label.
 *
 * With no `value` the tile shows "—" (read out as "loading" or "unavailable"),
 * and on an error its caption says the count could not be loaded. `attention`
 * sets the number in the failure colour the run badges use for
 * `failed_system` — the count is of failed runs, so the colour is the
 * lifecycle's own meaning, not a borrowed hue.
 */
function SummaryTile({
  label,
  value,
  pending,
  detail,
  attention = false,
}: {
  label: string;
  value: number | undefined;
  pending?: TilePending;
  detail?: React.ReactNode;
  attention?: boolean;
}): JSX.Element {
  const labelId = React.useId();
  const shown = pending === undefined ? value : undefined;
  return (
    <Card
      elevation="raised"
      role="group"
      aria-labelledby={labelId}
      className="flex flex-col gap-1 p-card"
    >
      <span id={labelId} className="text-base text-muted-foreground">
        {label}
      </span>
      {shown === undefined ? (
        <span className="font-mono text-3xl font-semibold text-muted-foreground">
          <span aria-hidden>—</span>
          <span className="sr-only">{pending === "error" ? "unavailable" : "loading"}</span>
        </span>
      ) : (
        <span
          className={cn(
            "font-mono text-3xl font-semibold tabular-nums",
            attention ? "text-state-failed-system-fg" : "text-foreground"
          )}
        >
          {formatCount(shown)}
        </span>
      )}
      {pending === "error" ? (
        <span className="text-xs text-muted-foreground">Could not load</span>
      ) : detail ? (
        <span className="text-xs text-muted-foreground">{detail}</span>
      ) : null}
    </Card>
  );
}

// ---- Start work -----------------------------------------------------

/**
 * The design's "start work" row, above the numbers.
 *
 * With no in-app card it is the Suite alone, full width, so it is only as
 * tall as its logos — exactly the page before FlowStack apps existed. With
 * cards (flowstack#1971), ONE card is a "Run <app> →" hero two columns wide
 * and the Suite narrows into the third, its logos two to a row. The card is
 * the FlowStack app this viewer used most recently (`heroCard`,
 * flowstack#1999); the rest stay on the Apps page. `/api/apps` returns only
 * the cards this session is entitled to.
 *
 * The runs feed is the same `useRuns` cache "Latest work" reads, so the hero
 * adds no request. Until it answers — or if it fails — the hero is chosen
 * from the card opened last alone.
 */
function StartWork(): JSX.Element {
  const { data: cards } = useInAppCards();
  const { data: session } = useSession();
  const last = useLastAppStore((s) => s.last);
  const { data: runs } = useRuns();
  const headingId = React.useId();
  const card = heroCard(cards ?? [], rememberedAppFor(last, session), runs);
  if (card === null) return <SuitePanel />;
  return (
    <section aria-labelledby={headingId} className="grid gap-4 lg:grid-cols-3">
      <h2 id={headingId} className="label-micro font-semibold text-muted-foreground lg:col-span-3">
        Start work
      </h2>
      <div className="flex lg:col-span-2">
        <InAppCard card={card} variant="hero" />
      </div>
      <SuitePanel compact />
    </section>
  );
}

// ---- ATTMOS Suite ---------------------------------------------------

/**
 * The portals this session is entitled to open, as a logo grid.
 * `/api/apps` returns the entitled subset (entitlement is decided
 * server-side from the Cognito groups).
 *
 * Each logo opens its portal in a new tab exactly as the launcher does:
 * `portalLaunchUrl`'s client-side domain gate (an app that fails it is
 * dropped, not linked), `target="_blank"` and `noopener noreferrer`. The
 * logo sits in the same `LogoWell` the launcher uses, so one portal never
 * picks a different theme variant on the two pages.
 *
 * `compact` is the narrow column beside the in-app heroes: two logos to a
 * row instead of six.
 */
function SuitePanel({ compact = false }: { compact?: boolean }): JSX.Element {
  const { data: apps, isLoading, isError, error } = useApps();
  const launchable = (apps ?? []).flatMap((app) => {
    const url = portalLaunchUrl(app);
    return url === null ? [] : [{ app, url }];
  });

  const ready = !isLoading && !isError && launchable.length > 0;

  return (
    <Panel
      title="ATTMOS Suite"
      to="/apps"
      linkLabel="All apps"
      description={ready ? "Each opens in a new tab with your ATTMOS sign-in." : undefined}
    >
      {isLoading ? (
        <PanelSkeleton />
      ) : isError ? (
        <PanelError error={error} subject="your apps" />
      ) : launchable.length === 0 ? (
        <EmptyState
          className="py-8"
          icon={<LayoutGrid className="h-7 w-7" aria-hidden />}
          title="No apps available to you yet."
          description="Ask a FlowStack admin for access, or open the launcher to request it from the owning team."
          cta={
            <Link
              to="/apps"
              className="text-sm font-medium text-accent underline-offset-4 hover:underline"
            >
              Open the launcher
            </Link>
          }
        />
      ) : (
        // Each tile names its app in visible text under the logo: a wordmark
        // alone does not name every portal, and a `title` tooltip never
        // shows on touch (R-1984-1). The name wraps rather than truncates:
        // with no tooltip, a clipped name could not be read in full (R-1984-4).
        <ul
          className={cn(
            "grid grid-cols-2 gap-3",
            compact ? "sm:grid-cols-3 lg:grid-cols-2" : "sm:grid-cols-3 lg:grid-cols-6"
          )}
        >
          {launchable.map(({ app, url }) => (
            <li key={app.slug}>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Open ${app.display_name} in a new tab`}
                className={cn(
                  "group flex flex-col gap-1.5 rounded-lg outline-none",
                  "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                )}
              >
                <span className="block rounded-lg transition-shadow group-hover:shadow-[var(--elevation-accent)]">
                  <LogoWell icon={app.icon} size="sm" />
                </span>
                <span className="break-words text-center text-xs font-medium text-foreground">
                  {app.display_name}
                </span>
              </a>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

// ---- Latest work ----------------------------------------------------

/** How many recent runs the summary shows before deferring to /runs. */
const LATEST_WORK_LIMIT = 6;

/**
 * The most recent runs, newest first, as a table. `/api/runs` is already
 * ordered most-recent-first server-side, so this slices rather than sorts.
 *
 * **No count is derived from this feed.** `/api/runs` is bounded
 * (`limit`, default 200), so "N runs in flight" computed here would be
 * silently capped — right on a pilot tenant, wrong on a busy one, and
 * wrong in the direction that under-reports. The totals live in the
 * summary strip, which reads counts the server computed.
 *
 * **Duration is printed only where the run record has both ends**
 * (`elapsedLabel`, the run header's own helper). A settled run without a
 * window says "not recorded" and an in-flight one keeps the dash —
 * `run-timing.ts` owns that wording, because a blank there once read as
 * "served from cache" across 300 runs.
 *
 * No polling loop is added here. `useRuns` carries a 10s `staleTime`
 * and TanStack refetches on window focus; a dashboard that opened its
 * own interval would be a second poller against the same endpoint the
 * runs page already polls.
 */
function LatestWorkPanel({ className }: { className?: string }): JSX.Element {
  const { data: runs, isLoading, isError, error } = useRuns();
  const latest = runs ? runs.slice(0, LATEST_WORK_LIMIT) : [];

  return (
    <Panel title="Latest work" titleSize="lg" to="/runs" linkLabel="All runs" className={className}>
      {isLoading ? (
        <PanelSkeleton />
      ) : isError ? (
        <PanelError error={error} subject="your latest runs" />
      ) : latest.length === 0 ? (
        <EmptyState
          className="py-8"
          icon={<ListChecks className="h-7 w-7" aria-hidden />}
          title="No runs yet."
          description="Submit a Tile or a Flow from a project — or from the SDK — and it will appear here as it progresses."
          cta={
            <Link
              to="/projects"
              className="text-sm font-medium text-accent underline-offset-4 hover:underline"
            >
              Pick a project to submit from
            </Link>
          }
        />
      ) : (
        <div className="flex min-w-0 flex-col gap-3">
          {/* States the truncation instead of implying these are all
              the runs there are. A bare list of six reads as "you have
              six runs" to anyone who does not already know better. */}
          <p className="text-sm text-muted-foreground">
            {`Your ${countLabel(latest.length, "most recent run")}.`}
          </p>
          {/* The table scrolls inside its own box at phone width rather
              than widening the page. */}
          <div className="overflow-x-auto">
            <Table className="min-w-[36rem]">
              <TableCaption>Latest work</TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead>Flow / tile</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead align="right">Duration</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {latest.map((run) => (
                  <RunRow key={run.run_id} run={run} />
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </Panel>
  );
}

/**
 * One run row. The status is the signature `RunStateBadge` — the same
 * component the runs grid and the DAG nodes render — so a state never
 * looks like one thing here and another on the page this links to, and
 * a failure names its class (`failed scientific`, `failed system`, …).
 */
function RunRow({ run }: { run: ProjectRun }): JSX.Element {
  const workload = run.flow_name ?? run.tile_name ?? run.run_id;
  return (
    <TableRow interactive="hover">
      <TableCell className="max-w-[16rem]">
        <Link
          to={`/runs/${encodeURIComponent(run.run_id)}`}
          className="inline-flex min-h-11 max-w-full items-center truncate font-mono text-foreground underline-offset-4 hover:text-accent hover:underline focus-visible:text-accent focus-visible:underline"
        >
          {workload}
        </Link>
      </TableCell>
      <TableCell className="text-muted-foreground">{run.project_slug ?? "no project"}</TableCell>
      <TableCell>
        <RunStateBadge state={run.status} />
      </TableCell>
      <TableCell className="whitespace-nowrap text-muted-foreground">
        <RelativeSubmitted at={run.submitted_at} />
      </TableCell>
      <TableCell align="right" className="whitespace-nowrap tabular-nums text-muted-foreground">
        {elapsedLabel(run) ?? absentTimingLabel(run.status)}
      </TableCell>
    </TableRow>
  );
}

/** "2m ago", with the absolute timestamp on hover. */
function RelativeSubmitted({ at }: { at: string }): JSX.Element {
  const when = new Date(at);
  return (
    <time dateTime={at} title={when.toLocaleString()}>
      {relativeTime(when)}
    </time>
  );
}

/** "1 run" / "3 runs" — pluralisation without a dependency. */
function countLabel(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}
